// Figma's icon set IS Phosphor - match it here instead of lucide lookalikes so
// the campaign nav icons line up with the main nav (House/Gear/etc.) and Figma.
import {
  Notebook,
  ChartBar,
  Users,
  UserCheck,
  FileText,
  FilmSlate,
  ChartLine,
  Gear,
  ShoppingBag,
} from '../../design-system/icons';
import type { NavItem } from '../../components/common/AppSidebar';
import type { CampaignPermissions } from '../../lib/campaignPermissions';
import type { CampaignPipeline } from '@discovr/types';
import { resolvePipeline } from './campaignPipeline';

export interface NavBuilderOptions {
  permissions: CampaignPermissions;
  counts: {
    creators?: number;
    confirmed?: number;
    scripts?: number;
    content?: number;
    liveContent?: number;
  };
  /** Show the Shopify nav item only when the campaign has Shopify enabled. */
  shopifyEnabled?: boolean;
  /** Canonical campaign platform. X drops the Scripts lane. */
  platform?: string | null;
  /** Production stages the campaign runs; absent = all. Disabled lanes drop their tab. */
  pipeline?: CampaignPipeline | null;
}

/**
 * Build the campaign detail sidebar navigation items from permissions and counts.
 * Both Brand and Admin wrappers call this instead of constructing nav inline.
 * Reporting and Live Content are visible to all roles.
 */
export function buildNavItems(options: NavBuilderOptions): NavItem[] {
  const { counts } = options;
  const pipeline = resolvePipeline(options.pipeline);

  const items: NavItem[] = [
    { id: 'reporting', label: 'Reporting', icon: ChartBar },
    { id: 'creators', label: 'Creators', icon: Users, count: counts.creators },
    { id: 'confirmed', label: 'Confirmed', icon: UserCheck, count: counts.confirmed },
  ];

  // A lane turned off stays visible while creators are still in it.
  if (pipeline.script_enabled || (counts.scripts ?? 0) > 0) {
    items.push({ id: 'scripts', label: 'Scripts', icon: FileText, count: counts.scripts });
  }

  if (pipeline.content_enabled || (counts.content ?? 0) > 0) {
    items.push({
      id: 'content',
      label: 'Content',
      icon: FilmSlate,
      count: counts.content,
    });
  }

  items.push({ id: 'live-content', label: 'Live', icon: ChartLine, count: counts.liveContent });

  // Shopify coupons - only shown when the campaign has Shopify enabled.
  if (options.shopifyEnabled) {
    items.push({ id: 'shopify', label: 'Shopify', icon: ShoppingBag });
  }

  // Details + Settings form the bottom cluster (matches Figma). A divider bar
  // above Details splits them from the primary nav group above. Details moved
  // down from the top to sit directly above Settings.
  items.push({ id: 'details', label: 'Details', icon: Notebook, dividerAbove: true });

  // Settings (Access + Integrations sub-tabs) - always visible; sub-tab
  // visibility is gated inside CampaignSettingsTab itself.
  items.push({ id: 'settings', label: 'Settings', icon: Gear });

  return items;
}
